import { ChevronRight } from "@untitledui/icons"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { HintText, InputBase, Label, TextField } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { TextEditor } from "@/components/ui/text-editor"
import { Toggle } from "@/components/ui/toggle"
import { cn } from "@/lib/utils"
import { formatDuration } from "@/lib/utils/"
import { useUrlPrefill } from "@/pages/tracks/_hooks/use-url-prefill"
import type { Tag } from "@/types"

import { setPrefillValues } from "../_helpers/set-prefill-value"
import { contentBodyAttributes } from "../tracks.constant"

import ContentBodyForms, { type TrackFormData } from "./content-body-forms"
import ContentTypeSelector from "./content-type-selector"
import TagFields from "./tag-fields"

interface TrackFormProps {
  data: TrackFormData
  setData: (key: "track", value: TrackFormData["track"]) => void
  errors: Record<string, unknown>
  availableTags: Tag[]
  initialTags?: Tag[]
  processing: boolean
  submitLabel: string
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
}

/**
 * TrackForm component
 * Desktop, Mobile view use the same layout thtat the fields sit in a single readable column.
 */
export default function TrackForm({
  data,
  setData,
  errors,
  availableTags,
  initialTags,
  processing,
  submitLabel,
  onSubmit,
  onCancel,
}: TrackFormProps) {
  const err = errors as Record<string, string | undefined>
  const post = data.track.post_attributes

  const [expanded, setExpanded] = useState(false)
  const [prefillError, setPrefillError] = useState<string | null>(null)

  /* Merge a partial update into post_attributes, leaving other fields intact */
  const setPost = (next: Partial<TrackFormData["track"]["post_attributes"]>) =>
    setData("track", {
      ...data.track,
      post_attributes: { ...post, ...next },
    })

  /* On each lookup result: apply the prefilled values and surface any error */
  const { loading, failed } = useUrlPrefill({
    url: post.canonical_url,
    onResult: (result) => {
      setPost(setPrefillValues(result, post))
      setPrefillError(
        result.status === "ok" || result.status === "partial"
          ? null
          : "We couldn't read this link automatically — add the details below.",
      )
      // Confident scrapes stay concise; anything short of that opens the
      // details so the fields needing a human are already in view. Only
      // ever opens — never collapses a section the user chose to expand.
      if (result.status !== "ok") setExpanded(true)
    },
  })

  /* Show the hint under the URL field */
  const lookupMessage = !post.canonical_url.trim()
    ? null
    : failed
      ? "We couldn't reach this link — add the details manually."
      : prefillError

  /* Non-empty detail fields joined for the collapsed Details header summary */
  const summaryParts = [
    post.title,
    post.content_body_attributes.source,
    `By ${post.creator}`,
    formatDuration(post.content_body_attributes.duration_seconds ?? undefined),
  ].filter(Boolean)

  return (
    <form onSubmit={onSubmit} className="mx-auto w-full max-w-2xl">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          {/* Content Body Type — mode switcher */}
          <ContentTypeSelector
            value={post.content_body_type}
            onChange={(newContentType) =>
              setPost({
                content_body_type: newContentType,
                content_body_attributes: contentBodyAttributes[newContentType],
              })
            }
          />

          {/* Content URL */}
          <TextField
            isRequired
            value={post.canonical_url}
            onChange={(v) => setPost({ canonical_url: v })}
            isInvalid={!!err["track.post_attributes.canonical_url"]}
          >
            <Label>Content URL</Label>
            <InputBase size="md" placeholder="https://..." />
            {loading && <HintText>Looking up this link…</HintText>}
            {!loading && lookupMessage && <HintText>{lookupMessage}</HintText>}
            {err["track.post_attributes.canonical_url"] && (
              <HintText isInvalid>
                {err["track.post_attributes.canonical_url"]}
              </HintText>
            )}
          </TextField>

          {/* Title */}
          <TextField
            isRequired
            value={post.title}
            onChange={(v) => setPost({ title: v })}
            isInvalid={!!err["track.post_attributes.title"]}
          >
            <Label>Title</Label>
            <InputBase size="md" placeholder="The title of the content" />
            {err["track.post_attributes.title"] && (
              <HintText isInvalid>
                {err["track.post_attributes.title"]}
              </HintText>
            )}
          </TextField>

          {/* Details & Expandable Summary */}
          <Label>Details</Label>
          <div className="border-secondary bg-primary overflow-hidden rounded-xl border">
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              className="hover:bg-secondary_subtle flex w-full items-center gap-3 px-4 py-3 text-left transition outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)] focus-visible:ring-inset motion-reduce:transition-none"
            >
              <ChevronRight
                className={cn(
                  "text-quaternary size-4 shrink-0 transition-transform",
                  expanded && "rotate-90",
                )}
              />
              {!expanded && post.thumbnail_url && (
                <img
                  src={post.thumbnail_url}
                  alt=""
                  className="size-20 shrink-0 rounded object-cover"
                />
              )}
              <div className="flex flex-col">
                {!expanded &&
                  summaryParts.length > 0 &&
                  summaryParts.map((summary, index) => (
                    <p
                      key={index}
                      className="text-tertiary min-w-0 truncate text-sm"
                    >
                      {summary}
                    </p>
                  ))}
              </div>
              <span className="text-brand-secondary ml-auto shrink-0 cursor-pointer text-sm font-semibold">
                {expanded ? "Hide" : "Edit"}
              </span>
            </button>

            <div
              className={cn(
                "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
                expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden" inert={!expanded}>
                <div className="flex flex-col gap-5 px-4 pt-1 pb-4">
                  {/* Thumbnail URL — preview beside the input */}
                  <div className="flex flex-col gap-3">
                    {post.thumbnail_url && (
                      <div className="bg-surface-grouped rounded-md">
                        <img
                          src={post.thumbnail_url}
                          alt=""
                          className="m-auto size-40 shrink-0 rounded object-cover"
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <TextField
                        value={post.thumbnail_url}
                        onChange={(v) => setPost({ thumbnail_url: v })}
                        isInvalid={!!err["track.post_attributes.thumbnail_url"]}
                      >
                        <Label>Thumbnail URL</Label>
                        <InputBase size="md" placeholder="https://..." />
                        {err["track.post_attributes.thumbnail_url"] && (
                          <HintText isInvalid>
                            {err["track.post_attributes.thumbnail_url"]}
                          </HintText>
                        )}
                      </TextField>
                    </div>
                  </div>

                  {/* Creator */}
                  <TextField
                    value={post.creator}
                    onChange={(v) => setPost({ creator: v })}
                    isInvalid={!!err["track.post_attributes.creator"]}
                  >
                    <Label>Creator</Label>
                    <InputBase size="md" placeholder="Creator name" />
                    {err["track.post_attributes.creator"] && (
                      <HintText isInvalid>
                        {err["track.post_attributes.creator"]}
                      </HintText>
                    )}
                  </TextField>

                  {/* Content-type specific fields */}
                  <ContentBodyForms
                    data={data}
                    setData={setData}
                    errors={errors}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          <TagFields
            availableTags={availableTags}
            initialSelectedTags={initialTags}
            tagIds={data.track.tag_ids}
            onTagIdsChange={(ids) =>
              setData("track", { ...data.track, tag_ids: ids })
            }
            error={err["track.tag_ids"]}
          />

          {/* Notes */}
          <TextEditor.Root
            content={data.track.notes ?? ""}
            onUpdate={({ editor }) =>
              setData("track", { ...data.track, notes: editor.getHTML() })
            }
            placeholder="Add note of your content. (optional)"
            isInvalid={!!err["track.notes"]}
            inputClassName="min-h-30 resize-y"
          >
            <TextEditor.Label>Notes</TextEditor.Label>
            <TextEditor.Toolbar type="simple" />
            <TextEditor.Content />
            {err["track.notes"] && (
              <TextEditor.HintText>{err["track.notes"]}</TextEditor.HintText>
            )}
          </TextEditor.Root>

          {/* Published toggle */}
          <Toggle
            isSelected={post.published}
            onChange={(isSelected) => setPost({ published: isSelected })}
            size="md"
            hint={
              post.published
                ? "This track is published and visible to everyone."
                : "This track is unpublished and only visible to you."
            }
            label={post.published ? "Published" : "Unpublished"}
            slim
          />
        </div>

        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing}
              isLoading={processing}
            >
              {submitLabel}
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </div>
    </form>
  )
}
